import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Editor from "@monaco-editor/react";
import {
  FiAlertCircle,
  FiBox,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiCode,
  FiCopy,
  FiFile,
  FiFolder,
  FiFolderPlus,
  FiGitBranch,
  FiGrid,
  FiImage,
  FiLayers,
  FiLock,
  FiSearch,
  FiShield,
  FiGlobe,
  FiDatabase,
  FiCloud,
  FiMessageSquare,
  FiPackage,
  FiSend,
  FiRefreshCw,
  FiX,
  FiZap,
} from "react-icons/fi";
import {
  SiAngular,
  SiC,
  SiCplusplus,
  SiCss,
  SiDocker,
  SiDotnet,
  SiGit,
  SiGnubash,
  SiGo,
  SiGraphql,
  SiHtml5,
  SiJavascript,
  SiJson,
  SiKotlin,
  SiLess,
  SiLua,
  SiMarkdown,
  SiMysql,
  SiNodedotjs,
  SiNpm,
  SiOpenjdk,
  SiPhp,
  SiPostgresql,
  SiPython,
  SiReact,
  SiRuby,
  SiRust,
  SiSass,
  SiSqlite,
  SiSvg,
  SiSvelte,
  SiSwift,
  SiToml,
  SiTypescript,
  SiVuedotjs,
  SiXml,
  SiYaml,
} from "react-icons/si";
import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import AgentService from "../../services/api/agents";
import CodeIntelligenceService from "../../services/api/codeIntelligence";
import { API_RESPONSE, default_size } from "../../settings/shared/constants";
import {
  formatFindingPrompt,
  stageFindingChatDraft,
  XRAY_JOB_TYPE,
} from "../../settings/shared/findingChatDraft";
import "../../assets/css/codeIntelligence.css";

const TABS = [
  ["overview", "Overview", FiGrid],
  ["architecture", "Architecture", FiLayers],
  ["modules", "Modules", FiBox],
  ["findings", "Findings", FiShield],
  ["discovery", "Discovery", FiCode],
  ["files", "Files", FiFolder],
  ["symbols", "Symbols", FiCode],
  ["graph", "Call graph", FiGitBranch],
];

const LANG_COLORS = ["var(--activemenu, #3d91e4)", "#10b981", "#8b5cf6", "#f59e0b", "#ec4899", "#06b6d4"];
const PAGE_SIZE = 15;

function formatBytes(n) {
  const v = Number(n) || 0;
  if (v < 1024) return `${v} B`;
  const units = ["KB", "MB", "GB"];
  let size = v / 1024;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size >= 10 ? size.toFixed(0) : size.toFixed(1)} ${units[i]}`;
}

function formatCount(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return v.toLocaleString();
}

function prettyReviewer(name) {
  return String(name || "reviewer")
    .replace(/_reviewer$/i, "")
    .replace(/_/g, " ");
}

function prettyLabel(value) {
  return String(value || "unknown").replace(/_/g, " ");
}

function looksLikeUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || ""));
}

function asPercent(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return n <= 1 ? Math.round(n * 100) : Math.round(n);
}

function barTone(score) {
  const n = Number(score) || 0;
  if (n >= 75) return "is-strong";
  if (n >= 40) return "is-mid";
  return "is-weak";
}

function asModules(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.modules)) return raw.modules;
  if (Array.isArray(raw.business_modules)) return raw.business_modules;
  return [];
}

function uniqueTypeScores(scores) {
  const best = new Map();
  (scores || []).forEach((item) => {
    const key = item.application_type || item.type || "unknown";
    const next = Number(item.normalized_score ?? item.score) || 0;
    const prev = best.get(key);
    if (!prev || next > (Number(prev.normalized_score ?? prev.score) || 0)) {
      best.set(key, item);
    }
  });
  return [...best.values()]
    .filter((item) => {
      const type = item.application_type || item.type;
      const score = Number(item.normalized_score) || 0;
      return type !== "unknown" || score > 0.25;
    })
    .sort((a, b) => (Number(b.normalized_score) || 0) - (Number(a.normalized_score) || 0));
}

const FILE_TYPE_META = {
  javascript: { Icon: SiJavascript, color: "#f7df1e" },
  jsx: { Icon: SiReact, color: "#61dafb" },
  typescript: { Icon: SiTypescript, color: "#3178c6" },
  tsx: { Icon: SiReact, color: "#61dafb" },
  python: { Icon: SiPython, color: "#3776ab" },
  html: { Icon: SiHtml5, color: "#e34f26" },
  css: { Icon: SiCss, color: "#1572b6" },
  scss: { Icon: SiSass, color: "#cc6699" },
  less: { Icon: SiLess, color: "#1d365d" },
  json: { Icon: SiJson, color: "#292929" },
  yaml: { Icon: SiYaml, color: "#cb171e" },
  xml: { Icon: SiXml, color: "#005fad" },
  markdown: { Icon: SiMarkdown, color: "#000000" },
  go: { Icon: SiGo, color: "#00add8" },
  rust: { Icon: SiRust, color: "#000000" },
  java: { Icon: SiOpenjdk, color: "#437291" },
  php: { Icon: SiPhp, color: "#777bb4" },
  ruby: { Icon: SiRuby, color: "#cc342d" },
  c: { Icon: SiC, color: "#a8b9cc" },
  cpp: { Icon: SiCplusplus, color: "#00599c" },
  csharp: { Icon: SiDotnet, color: "#512bd4" },
  shell: { Icon: SiGnubash, color: "#4eaa25" },
  docker: { Icon: SiDocker, color: "#2496ed" },
  git: { Icon: SiGit, color: "#f05032" },
  npm: { Icon: SiNpm, color: "#cb3837" },
  node: { Icon: SiNodedotjs, color: "#339933" },
  vue: { Icon: SiVuedotjs, color: "#4fc08d" },
  angular: { Icon: SiAngular, color: "#dd0031" },
  svelte: { Icon: SiSvelte, color: "#ff3e00" },
  graphql: { Icon: SiGraphql, color: "#e10098" },
  sql: { Icon: SiMysql, color: "#4479a1" },
  postgres: { Icon: SiPostgresql, color: "#4169e1" },
  sqlite: { Icon: SiSqlite, color: "#003b57" },
  kotlin: { Icon: SiKotlin, color: "#7f52ff" },
  swift: { Icon: SiSwift, color: "#f05138" },
  lua: { Icon: SiLua, color: "#2c2d72" },
  toml: { Icon: SiToml, color: "#9c4221" },
  svg: { Icon: SiSvg, color: "#ffb13b" },
  image: { Icon: FiImage, color: "#0ea5e9" },
  lock: { Icon: FiLock, color: "#64748b" },
  default: { Icon: FiFile, color: "#64748b" },
};

const EXT_TO_KIND = {
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  jsx: "jsx",
  ts: "typescript",
  mts: "typescript",
  cts: "typescript",
  tsx: "tsx",
  py: "python",
  pyw: "python",
  html: "html",
  htm: "html",
  css: "css",
  scss: "scss",
  sass: "scss",
  less: "less",
  json: "json",
  jsonc: "json",
  yml: "yaml",
  yaml: "yaml",
  xml: "xml",
  xsd: "xml",
  md: "markdown",
  mdx: "markdown",
  go: "go",
  rs: "rust",
  java: "java",
  php: "php",
  rb: "ruby",
  c: "c",
  h: "c",
  cc: "cpp",
  cpp: "cpp",
  cxx: "cpp",
  hpp: "cpp",
  cs: "csharp",
  sh: "shell",
  bash: "shell",
  zsh: "shell",
  kts: "kotlin",
  kt: "kotlin",
  swift: "swift",
  lua: "lua",
  toml: "toml",
  sql: "sql",
  graphql: "graphql",
  gql: "graphql",
  vue: "vue",
  svelte: "svelte",
  svg: "svg",
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  ico: "image",
  lock: "lock",
};

const LANG_TO_KIND = {
  javascript: "javascript",
  jsx: "jsx",
  typescript: "typescript",
  tsx: "tsx",
  python: "python",
  html: "html",
  css: "css",
  scss: "scss",
  sass: "scss",
  less: "less",
  json: "json",
  yaml: "yaml",
  xml: "xml",
  markdown: "markdown",
  go: "go",
  golang: "go",
  rust: "rust",
  java: "java",
  php: "php",
  ruby: "ruby",
  c: "c",
  "c++": "cpp",
  cpp: "cpp",
  "c#": "csharp",
  csharp: "csharp",
  shell: "shell",
  bash: "shell",
  docker: "docker",
  dockerfile: "docker",
  git: "git",
  vue: "vue",
  angular: "angular",
  svelte: "svelte",
  graphql: "graphql",
  sql: "sql",
  kotlin: "kotlin",
  swift: "swift",
  lua: "lua",
  toml: "toml",
  svg: "svg",
};

function fileKind(path, language) {
  const raw = String(path || "").trim();
  const name = raw.split("/").pop() || "";
  const lower = name.toLowerCase();
  const lang = String(language || "").toLowerCase().trim();
  if (lower === "dockerfile" || lower.startsWith("dockerfile.")) return "docker";
  if (lower === "makefile") return "shell";
  if (lower === "package.json" || lower === "package-lock.json" || lower === "pnpm-lock.yaml" || lower === "yarn.lock") {
    return "npm";
  }
  if (lower === ".gitignore" || lower === ".gitattributes" || lower.startsWith(".git")) return "git";
  if (lower.endsWith(".lock") || lower.endsWith("-lock.json") || lower.endsWith(".lock.json")) return "lock";
  if (LANG_TO_KIND[lang]) return LANG_TO_KIND[lang];
  const ext = lower.includes(".") ? lower.split(".").pop() : "";
  return EXT_TO_KIND[ext] || "default";
}

function FileTypeIcon({ path, language, size = 14 }) {
  const kind = fileKind(path, language);
  const meta = FILE_TYPE_META[kind] || FILE_TYPE_META.default;
  const Icon = meta.Icon;
  return (
    <span className="ci-file-icon" title={kind} aria-hidden="true">
      <Icon size={size} color={meta.color} />
    </span>
  );
}

function FilePath({ path, language, className = "", onClick }) {
  if (!path) return "—";
  const inner = (
    <>
      <FileTypeIcon path={path} language={language} />
      <span className="ci-path">{path}</span>
    </>
  );
  if (!onClick) {
    return <span className={`ci-file ${className}`.trim()}>{inner}</span>;
  }
  return (
    <button type="button" className={`ci-file is-link ${className}`.trim()} onClick={() => onClick(path)}>
      {inner}
    </button>
  );
}

function normalizeFilePath(path) {
  return String(path || "")
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\/+/, "");
}

function findIndexedFile(files, path) {
  const target = normalizeFilePath(path);
  if (!target) return null;
  return (
    files.find((file) => normalizeFilePath(file.path) === target) ||
    files.find((file) => {
      const current = normalizeFilePath(file.path);
      return current.endsWith(`/${target}`) || current.endsWith(target);
    }) ||
    null
  );
}

function findingPath(finding) {
  const loc = finding?.location || finding?.locations?.[0] || {};
  return finding?.file || finding?.path || loc.file || loc.path || loc.uri || "";
}

function findingLine(finding) {
  const loc = finding?.location || finding?.locations?.[0] || {};
  const start = loc.start || loc.region || {};
  const raw =
    finding?.line ??
    finding?.line_number ??
    finding?.start_line ??
    finding?.startLine ??
    finding?.lineno ??
    loc.line ??
    loc.start_line ??
    loc.startLine ??
    start.line ??
    start.start_line;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function folderAncestors(path) {
  const parts = normalizeFilePath(path).split("/").filter(Boolean);
  const folders = new Set();
  parts.slice(0, -1).forEach((_, i) => folders.add(parts.slice(0, i + 1).join("/")));
  return folders;
}

function monacoLanguage(path, language) {
  const name = String(path || "").split("/").pop() || "";
  const lower = name.toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop() : "";
  if (lower === "dockerfile" || lower.startsWith("dockerfile.")) return "dockerfile";
  const fromExt = {
    js: "javascript",
    jsx: "javascript",
    mjs: "javascript",
    cjs: "javascript",
    ts: "typescript",
    tsx: "typescript",
    java: "java",
    py: "python",
    json: "json",
    jsonc: "json",
    html: "html",
    htm: "html",
    css: "css",
    scss: "scss",
    less: "less",
    xml: "xml",
    yml: "yaml",
    yaml: "yaml",
    md: "markdown",
    mdx: "markdown",
    sh: "shell",
    bash: "shell",
    zsh: "shell",
    sql: "sql",
    go: "go",
    rs: "rust",
    php: "php",
    rb: "ruby",
    kt: "kotlin",
    kts: "kotlin",
    cs: "csharp",
    c: "c",
    h: "c",
    cpp: "cpp",
    cc: "cpp",
    cxx: "cpp",
    hpp: "cpp",
    swift: "swift",
    lua: "lua",
    toml: "ini",
    ini: "ini",
    graphql: "graphql",
    gql: "graphql",
    vue: "html",
    svelte: "html",
  };
  if (fromExt[ext]) return fromExt[ext];
  const lang = String(language || "").toLowerCase();
  const fromLang = {
    javascript: "javascript",
    jsx: "javascript",
    typescript: "typescript",
    tsx: "typescript",
    python: "python",
    java: "java",
    json: "json",
    html: "html",
    css: "css",
    scss: "scss",
    yaml: "yaml",
    markdown: "markdown",
    go: "go",
    golang: "go",
    rust: "rust",
    php: "php",
    ruby: "ruby",
    shell: "shell",
    bash: "shell",
    sql: "sql",
    csharp: "csharp",
    "c#": "csharp",
    "c++": "cpp",
    cpp: "cpp",
    kotlin: "kotlin",
    swift: "swift",
  };
  return fromLang[lang] || "plaintext";
}

function CodePreview({ content, path, language, highlightLine }) {
  const editorRef = useRef(null);
  const decorationsRef = useRef([]);

  const applyHighlight = (editor) => {
    if (!editor) return;
    const line = Number(highlightLine);
    if (!Number.isFinite(line) || line < 1) {
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);
      return;
    }
    editor.revealLineInCenter(line);
    decorationsRef.current = editor.deltaDecorations(decorationsRef.current, [
      {
        range: {
          startLineNumber: line,
          startColumn: 1,
          endLineNumber: line,
          endColumn: 1,
        },
        options: {
          isWholeLine: true,
          className: "ci-code-line-active",
          linesDecorationsClassName: "ci-code-line-gutter",
        },
      },
    ]);
  };

  useEffect(() => {
    applyHighlight(editorRef.current);
  }, [highlightLine, content, path]);

  return (
    <div className="ci-files-preview-code">
      <Editor
        height="100%"
        language={monacoLanguage(path, language)}
        value={String(content ?? "")}
        theme="vs"
        path={path || "untitled"}
        onMount={(editor, monaco) => {
          editorRef.current = editor;
          monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: true,
            noSuggestionDiagnostics: true,
          });
          monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
            noSemanticValidation: true,
            noSyntaxValidation: true,
            noSuggestionDiagnostics: true,
          });
          applyHighlight(editor);
        }}
        options={{
          readOnly: true,
          domReadOnly: true,
          minimap: { enabled: false },
          lineNumbers: "on",
          scrollBeyondLastLine: false,
          wordWrap: "off",
          fontSize: 13,
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          automaticLayout: true,
          renderLineHighlight: highlightLine ? "none" : "line",
          contextmenu: false,
          folding: true,
          stickyScroll: { enabled: true },
          padding: { top: 12, bottom: 12 },
          lineNumbersMinChars: 3,
          glyphMargin: false,
          renderWhitespace: "none",
          smoothScrolling: true,
        }}
      />
    </div>
  );
}

function buildFileTree(fileList) {
  const root = { name: "", path: "", type: "folder", children: [], map: {} };
  fileList.forEach((file) => {
    const parts = String(file.path || "").split("/").filter(Boolean);
    if (!parts.length) return;
    let node = root;
    parts.forEach((part, index) => {
      const isFile = index === parts.length - 1;
      const path = parts.slice(0, index + 1).join("/");
      if (!node.map[part]) {
        const child = isFile
          ? { name: part, path, type: "file", file }
          : { name: part, path, type: "folder", children: [], map: {} };
        node.map[part] = child;
        node.children.push(child);
      } else if (isFile) {
        node.map[part].file = file;
        node.map[part].type = "file";
      }
      node = node.map[part];
    });
  });

  const sortNode = (node) => {
    if (!node.children) return;
    node.children.sort((a, b) => {
      if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });
    node.children.forEach(sortNode);
  };
  sortNode(root);
  return root.children;
}

function countTreeFiles(node) {
  if (!node) return 0;
  if (node.type === "file") return 1;
  return (node.children || []).reduce((sum, child) => sum + countTreeFiles(child), 0);
}

function FileBreadcrumb({ path }) {
  const parts = normalizeFilePath(path).split("/").filter(Boolean);
  if (!parts.length) return null;
  return (
    <nav className="ci-files-crumbs" aria-label="File path">
      {parts.map((part, i) => (
        <span key={`${part}-${i}`} className={i === parts.length - 1 ? "is-file" : "is-folder"}>
          {i > 0 ? <span className="ci-files-crumb-sep">/</span> : null}
          {part}
        </span>
      ))}
    </nav>
  );
}

function FileTreeNode({ node, expanded, selectedPath, onToggle, onOpen }) {
  const isFolder = node.type === "folder";
  const open = isFolder && expanded.has(node.path);
  const selected = !isFolder && selectedPath === node.path;
  const file = node.file || { path: node.path };
  const fileCount = isFolder ? countTreeFiles(node) : 0;

  return (
    <div className="ci-tree-node">
      <button
        type="button"
        className={`ci-tree-row${isFolder ? " is-folder" : " is-file"}${open ? " is-open" : ""}${selected ? " is-selected" : ""}`}
        onClick={() => (isFolder ? onToggle(node.path) : onOpen(file))}
        title={node.path}
      >
        <span className="ci-tree-chevron">
          {isFolder ? (open ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />) : null}
        </span>
        {isFolder ? (
          open ? <FiFolderPlus size={14} className="ci-tree-folder" /> : <FiFolder size={14} className="ci-tree-folder" />
        ) : (
          <FileTypeIcon path={file.path} language={file.language} size={14} />
        )}
        <span className="ci-tree-name">{node.name}</span>
        {isFolder && fileCount ? <span className="ci-tree-count">{fileCount}</span> : null}
      </button>
      {isFolder && open ? (
        <div className="ci-tree-children">
          {(node.children || []).map((child) => (
            <FileTreeNode
              key={child.path}
              node={child}
              expanded={expanded}
              selectedPath={selectedPath}
              onToggle={onToggle}
              onOpen={onOpen}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function isGitPath(path) {
  const p = String(path || "");
  return p === ".git" || p.startsWith(".git/");
}

function asList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "object") return Object.values(value).flat();
  return [];
}

function itemText(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return [
    item.path,
    item.file,
    item.name,
    item.module,
    item.host,
    item.url,
    item.method,
    item.type,
    item.kind,
    item.source,
    item.target,
    item.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function methodClass(method) {
  const m = String(method || "").toUpperCase();
  if (m === "GET") return "get";
  if (m === "POST") return "post";
  if (m === "PUT" || m === "PATCH") return "put";
  if (m === "DELETE") return "del";
  return "other";
}

function flattenSymbols(index) {
  const raw = index?.symbols;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    return Object.values(raw)
      .flat()
      .map((s) => (typeof s === "string" ? { name: s } : s))
      .filter(Boolean);
  }
  return (index?.semantic?.documents || []).flatMap((doc) =>
    (doc.symbols || []).map((s) => {
      const obj = typeof s === "string" ? { name: s } : s || {};
      return { ...obj, file: obj.file || doc.id, language: obj.language || doc.language };
    })
  );
}

function recParts(item) {
  const text = String(item || "");
  const tag = text.match(/\[([A-Z][A-Z0-9_]*)\]/);
  return {
    tag: tag ? prettyLabel(tag[1]) : "",
    body: text.replace(/\s*\[[A-Z][A-Z0-9_]*\]\s*/g, " ").replace(/\s+/g, " ").trim() || text,
  };
}

function uniqueList(items) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = String(item || "").trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function groupSignalFlags(flags) {
  const buckets = { Structure: [], Runtime: [], Risk: [], Other: [] };
  (flags || []).forEach((flag) => {
    const key = String(flag).toLowerCase();
    if (/(coupl|test|legacy|sync|dead|orphan|god)/.test(key)) buckets.Risk.push(flag);
    else if (/(api|http|rout|cli|framework|worker|queue|grpc|graphql|endpoint)/.test(key)) buckets.Runtime.push(flag);
    else if (/(module|deploy|internal|package|layer|monolith|service)/.test(key)) buckets.Structure.push(flag);
    else buckets.Other.push(flag);
  });
  return Object.entries(buckets).filter(([, items]) => items.length);
}

function MiniRing({ score, label }) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  const size = 88;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const tone = barTone(n);
  return (
    <div className={`ci-mini-ring ${tone}`} aria-label={`${label || "Score"} ${n}%`}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - n / 100)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="ci-mini-ring-label">
        <strong>{n}%</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

function HealthRing({ score }) {
  const size = 112;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const offset = c * (1 - clamped / 100);
  const tone = barTone(clamped);
  const start = tone === "is-strong" ? "#0f766e" : tone === "is-weak" ? "#f59e0b" : "var(--activemenu, #3d91e4)";
  const end = tone === "is-strong" ? "#34d399" : tone === "is-weak" ? "#fb7185" : "#38bdf8";
  return (
    <div className="ci-ring" aria-label={`Health score ${clamped}`}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle className="ci-ring-track" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="url(#ciHealthGrad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
        <defs>
          <linearGradient id="ciHealthGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={start} />
            <stop offset="100%" stopColor={end} />
          </linearGradient>
        </defs>
      </svg>
      <div className="ci-ring-label">
        <div className={`ci-ring-score ${tone}`}>{Math.round(clamped)}</div>
        <div className="ci-ring-sub">Health</div>
      </div>
    </div>
  );
}

const SCAN_LOAD_STAGES = [
  { id: "index", label: "Indexing the codebase", hint: "Files, languages, and modules" },
  { id: "arch", label: "Mapping architecture", hint: "Patterns, boundaries, and type" },
  { id: "health", label: "Scoring application health", hint: "Security, quality, and risk" },
  { id: "findings", label: "Collecting findings", hint: "Issues, APIs, and dependencies" },
];

function CiInsightsLoading({ projectName }) {
  const [stageIndex, setStageIndex] = useState(0);
  const [kpiSlot, setKpiSlot] = useState(null);

  useLayoutEffect(() => {
    setKpiSlot(document.getElementById("ci-kpi-slot"));
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setStageIndex((current) => (current + 1) % SCAN_LOAD_STAGES.length);
    }, 1100);
    return () => window.clearInterval(timer);
  }, []);

  const title = projectName || "Your Xray project";
  const kpiStrip = (
    <div className="ci-kpi-strip ci-loading-kpis" aria-hidden="true">
      {["Files", "Modules", "Findings", "Architecture"].map((label) => (
        <div key={label} className="ci-kpi-chip is-loading">
          <div className="label">{label}</div>
          <div className="ci-loading-bar ci-loading-bar-sm" />
        </div>
      ))}
    </div>
  );

  return (
    <>
      {kpiSlot ? createPortal(kpiStrip, kpiSlot) : null}
      <div className="ci-dash ci-loading-dash" aria-busy="true" aria-live="polite">
        <div className="ci-loading-beam" aria-hidden="true" />
        <section className="ci-story ci-loading-story">
          <div className="ci-story-main">
            <div className="ci-loading-ring" aria-hidden="true">
              <span className="ci-loading-orbit" />
              <span className="ci-loading-orbit is-late" />
              <div className="ci-loading-ring-core">
                <FiSearch size={22} />
              </div>
            </div>
            <div className="ci-story-copy">
              <div className="ci-story-kicker">Scanning in progress</div>
              <h2>{title}</h2>
              <p>Assembling architecture, health, and findings for this Xray project.</p>
              <div className="ci-loading-stages">
                {SCAN_LOAD_STAGES.map((stage, index) => {
                  const done = index < stageIndex;
                  const active = index === stageIndex;
                  return (
                    <div
                      key={stage.id}
                      className={`ci-loading-stage${done ? " is-done" : ""}${active ? " is-active" : ""}`}
                    >
                      <span className="ci-loading-stage-mark">
                        {done ? <FiCheckCircle size={14} /> : <span />}
                      </span>
                      <span>
                        {stage.label}
                        {active ? <em>{stage.hint}</em> : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="ci-cats ci-loading-cats" aria-hidden="true">
            {["Security", "Quality", "Architecture", "Dependency", "Compliance"].map((label) => (
              <div className="ci-cat" key={label}>
                <div className="ci-cat-top">
                  <span className="ci-cat-name">{label}</span>
                  <span className="ci-loading-bar ci-loading-bar-xs" />
                </div>
                <div className="ci-cat-track">
                  <div className="ci-loading-fill" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="ci-tabs" aria-hidden="true">
          {["Overview", "Architecture", "Modules", "Findings"].map((label, index) => (
            <button key={label} type="button" className={index === 0 ? "is-active" : ""} disabled>
              {label}
            </button>
          ))}
        </div>

        <div className="ci-grid-3" aria-hidden="true">
          {["Languages", "Reviewers", "Discovery"].map((titleLabel) => (
            <article className="ci-card ci-loading-card" key={titleLabel}>
              <h3 className="ci-card-title">{titleLabel}</h3>
              <div className="ci-loading-bar" />
              <div className="ci-loading-bar ci-loading-bar-md" />
              <div className="ci-loading-bar ci-loading-bar-sm" />
            </article>
          ))}
        </div>
      </div>
    </>
  );
}

function Pager({ page, pages, onPage, total, noun }) {
  if (!total) return null;
  return (
    <div className="ci-pagination">
      <span>
        {total.toLocaleString()} {noun}
        {pages > 1 ? ` · ${page}/${pages}` : ""}
      </span>
      {pages > 1 ? (
        <>
          <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
          <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
        </>
      ) : null}
    </div>
  );
}

function usePaged(items, pageSize = PAGE_SIZE, resetKey) {
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [resetKey]);
  const pages = Math.max(1, Math.ceil((items.length || 0) / pageSize) || 1);
  const safePage = Math.min(page, pages);
  return {
    page: safePage,
    setPage,
    pages,
    slice: items.slice((safePage - 1) * pageSize, safePage * pageSize),
    total: items.length,
  };
}

function DiscoveryPanel({ id, icon: Icon, title, hint, count, children }) {
  const [open, setOpen] = useState(count > 0);
  return (
    <article className={`ci-disc-panel${open ? " is-open" : ""}`} id={id}>
      <button type="button" className="ci-disc-head" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="ci-disc-ico" aria-hidden="true">
          <Icon size={16} />
        </span>
        <span className="ci-disc-copy">
          <strong>{title}</strong>
          {hint ? <em>{hint}</em> : null}
        </span>
        <span className="ci-disc-count">{count.toLocaleString()}</span>
        <FiChevronDown className="ci-disc-chev" size={16} />
      </button>
      {open ? <div className="ci-disc-body">{children}</div> : null}
    </article>
  );
}

function FilterTable({ items, columns, placeholder, extra, noun = "rows", empty = "No matching rows." }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => itemText(item).includes(q));
  }, [items, query]);
  const paged = usePaged(filtered, PAGE_SIZE, query);

  return (
    <>
      <div className="ci-toolbar">
        <input type="text" placeholder={placeholder} value={query} onChange={(e) => setQuery(e.target.value)} />
        {extra}
        <span className="count">{filtered.length.toLocaleString()} shown</span>
      </div>
      {filtered.length ? (
        <div className="ci-table-wrap">
          <table className="ci-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.slice.map((row, i) => (
                <tr key={row.id || row.path || `${row.name || row.module || row.target}-${i}`}>
                  {columns.map((col) => (
                    <td key={col.key} className={col.className}>
                      {col.render ? col.render(row) : row[col.key] ?? "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ci-empty">{empty}</div>
      )}
      <Pager page={paged.page} pages={paged.pages} onPage={paged.setPage} total={paged.total} noun={noun} />
    </>
  );
}

const MODERNIZE_POLL_MS = 1500;
const MODERNIZE_META_KEYS = new Set(["compression", "source_job_id"]);

function readApiError(payload, fallback) {
  const detail = payload?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const text = detail.map((item) => item?.msg || item?.message || String(item)).filter(Boolean).join("; ");
    if (text) return text;
  }
  return payload?.errorMessage || payload?.error || payload?.message || fallback;
}

function isFailedStatus(value) {
  const status = String(value || "").toUpperCase();
  return status === API_RESPONSE.FAILED || status === API_RESPONSE.FAILURE || status === "FAILED" || status === "FAILURE";
}

function isCompletedStatus(value) {
  const status = String(value || "").toLowerCase();
  return status === "completed" || status === "complete" || status === "success" || status === "succeeded";
}

function modernizeDocumentation(payload) {
  return payload?.documentation
    || payload?.data?.documentation
    || payload?.result?.documentation
    || null;
}

function jsonPreviewText(data) {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return "";
  }
}

function asJsonDocument(value) {
  if (value && typeof value === "object") return value;
  if (typeof value !== "string" || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function modernizeDocument(payload) {
  const documentation = modernizeDocumentation(payload);
  if (!documentation || typeof documentation !== "object" || Array.isArray(documentation)) {
    return asJsonDocument(documentation);
  }
  const body = {};
  Object.keys(documentation).forEach((key) => {
    if (MODERNIZE_META_KEYS.has(key)) return;
    const parsed = asJsonDocument(documentation[key]);
    if (parsed) body[key] = parsed;
  });
  const ordered = {};
  ["functional", "technical", ...Object.keys(body)].forEach((key) => {
    if (body[key] && !ordered[key]) ordered[key] = body[key];
  });
  return Object.keys(ordered).length ? ordered : null;
}

function buildModernizeJson(payload) {
  const doc = modernizeDocument(payload);
  return doc ? jsonPreviewText(doc) : "";
}

const CodeIntelligenceOverview = ({
  data,
  loading = false,
  projectName = "",
  xrayProjectId = "",
  productId = "",
  onReanalyze,
  reanalyzing = false,
  canReanalyze = false,
  onRefresh,
  refreshing = false,
  onFileOpen,
  onTabChange,
  fileContent,
  fileContentLoading = false,
  fileContentError = "",
}) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [fileQuery, setFileQuery] = useState("");
  const [fileLang, setFileLang] = useState("all");
  const [showGit, setShowGit] = useState(false);
  const [findingSev, setFindingSev] = useState("all");
  const [openModule, setOpenModule] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(() => new Set());
  const [selectedFile, setSelectedFile] = useState(null);
  const [highlightLine, setHighlightLine] = useState(null);
  const [kpiSlot, setKpiSlot] = useState(null);
  const [confirmReanalyze, setConfirmReanalyze] = useState(false);
  const [modernizeOpen, setModernizeOpen] = useState(false);
  const [modernizing, setModernizing] = useState(false);
  const [modernizeStatus, setModernizeStatus] = useState("");
  const [modernizeError, setModernizeError] = useState("");
  const [modernizeResult, setModernizeResult] = useState(null);
  const modernizeCancelRef = useRef(false);
  const modernizePollRef = useRef(null);
  const scrollFileIntoViewRef = useRef(false);
  const treeBodyRef = useRef(null);

  useLayoutEffect(() => {
    setKpiSlot(document.getElementById("ci-kpi-slot"));
  }, []);

  useEffect(() => {
    onTabChange?.(tab);
    return () => onTabChange?.("");
  }, [tab, onTabChange]);

  const application = data?.application || {};
  const overview = data?.overview || {};
  const health = overview.health || {};
  const inventory = overview.inventory || {};
  const metrics = data?.metrics || application.metrics || {};
  const categories = health.categories || {};
  const severityCounts = health.severity_counts || {};
  const index = data?.index || {};
  const graph = data?.graph || index.graph || {};
  const files = index.files || [];
  const reviews = data?.reviews || [];
  const discovery = data?.discovery || {};
  const architecture = data?.architecture || application.architecture || {};
  const assessment = architecture.assessment || architecture;
  const architectureSignals = architecture.signals || {};
  const businessModulesRaw = data?.business_modules || application.business_modules || {};
  const manifests = data?.detection?.manifests || application.manifests || [];
  const dependencies = index.dependencies || [];
  const findings = useMemo(
    () => (data?.analysis?.findings?.length ? data.analysis.findings : overview.top_findings || []),
    [data, overview.top_findings]
  );

  const languages = overview.languages || [];
  const frameworks = overview.frameworks?.length ? overview.frameworks : application.frameworks || [];
  const displayName = useMemo(() => {
    const candidates = [projectName, application.name, overview.name, manifests[0]?.name, application.repo];
    return candidates.find((name) => name && !looksLikeUuid(name)) || projectName || "Imported application";
  }, [projectName, application.name, application.repo, overview.name, manifests]);
  const modules = useMemo(() => asModules(businessModulesRaw), [businessModulesRaw]);
  const typeScores = useMemo(() => uniqueTypeScores(assessment.scores), [assessment.scores]);
  const primaryType = assessment.primary_type || typeScores[0]?.application_type;
  const architectureConfidence = asPercent(assessment.confidence ?? typeScores[0]?.normalized_score);
  const legacy = assessment.legacy || {};
  const signalFlags = useMemo(() => {
    const skip = new Set(["business_modules", "file_count", "parsed_file_count", "symbol_count", "function_count", "class_count"]);
    return Object.entries(architectureSignals)
      .filter(([key, value]) => !skip.has(key) && value === true)
      .map(([key]) => prettyLabel(key));
  }, [architectureSignals]);
  const signalGroups = useMemo(() => groupSignalFlags(signalFlags), [signalFlags]);
  const architectureRecs = useMemo(
    () => uniqueList([...(assessment.recommendations || []), ...(legacy.recommendations || [])]).slice(0, 8),
    [assessment.recommendations, legacy.recommendations]
  );

  const symbols = useMemo(() => flattenSymbols(index), [index]);
  const imports = useMemo(
    () =>
      (graph.imports || []).map((e) => ({
        module: e.target || e.module,
        file: e.source || e.file,
        type: e.type,
      })),
    [graph.imports]
  );
  const callEdges = graph.calls;

  const callBars = useMemo(() => {
    const map = {};
    (callEdges || []).forEach((c) => {
      const key = c.target || "unknown";
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .map(([target, count]) => ({ target, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  }, [callEdges]);

  const gitCount = useMemo(() => files.filter((f) => isGitPath(f.path)).length, [files]);
  const languagesInFiles = useMemo(() => {
    const set = new Set();
    files.forEach((f) => {
      if (f.language) set.add(f.language);
    });
    return [...set].sort();
  }, [files]);

  const visibleFiles = useMemo(() => {
    const q = fileQuery.trim().toLowerCase();
    return files.filter((f) => {
      if (!showGit && isGitPath(f.path)) return false;
      if (fileLang !== "all" && f.language !== fileLang) return false;
      if (q && !String(f.path).toLowerCase().includes(q)) return false;
      return true;
    });
  }, [files, fileQuery, fileLang, showGit]);

  const fileTree = useMemo(() => buildFileTree(visibleFiles), [visibleFiles]);

  useEffect(() => {
    const query = fileQuery.trim();
    if (!query) return;
    const next = new Set();
    visibleFiles.forEach((file) => {
      const parts = String(file.path || "").split("/").filter(Boolean);
      parts.slice(0, -1).forEach((_, i) => next.add(parts.slice(0, i + 1).join("/")));
    });
    setExpandedFolders(next);
  }, [fileQuery, visibleFiles]);

  useEffect(() => {
    if (tab !== "files" || !scrollFileIntoViewRef.current || !selectedFile?.path) return;
    scrollFileIntoViewRef.current = false;
    const id = window.requestAnimationFrame(() => {
      const selected = treeBodyRef.current?.querySelector(".ci-tree-row.is-selected");
      selected?.scrollIntoView({ block: "nearest", inline: "nearest" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [tab, selectedFile?.path, expandedFolders]);

  const toggleFolder = (path) => {
    const body = treeBodyRef.current;
    const top = body?.scrollTop ?? 0;
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
    requestAnimationFrame(() => {
      if (body) body.scrollTop = top;
    });
  };

  const handleFileOpen = (file) => {
    setSelectedFile(file);
    setHighlightLine(null);
    onFileOpen?.(file?.path, file);
  };

  const openFindingLocation = (finding) => {
    const rawPath = findingPath(finding);
    if (!rawPath) return;
    const match = findIndexedFile(files, rawPath);
    const file = match || { path: normalizeFilePath(rawPath) };
    const line = findingLine(finding);
    if (isGitPath(file.path)) setShowGit(true);
    setFileQuery("");
    setFileLang("all");
    setTab("files");
    setSelectedFile(file);
    setHighlightLine(line);
    setExpandedFolders(folderAncestors(file.path));
    scrollFileIntoViewRef.current = true;
    if (selectedFile?.path !== file.path || fileContent == null) {
      onFileOpen?.(file.path, file);
    }
  };

  const resolvedProductId = String(
    productId ||
    application.product_id ||
    application.productId ||
    data?.product_id ||
    ""
  ).trim();
  const modernizeJsonText = useMemo(() => buildModernizeJson(modernizeResult), [modernizeResult]);
  const [jsonCopied, setJsonCopied] = useState(false);

  useEffect(() => () => {
    modernizeCancelRef.current = true;
    if (modernizePollRef.current) clearTimeout(modernizePollRef.current);
  }, []);

  const waitForModernizeJob = useCallback((started) => new Promise((resolve, reject) => {
    const statusUrl = started?.status_url || started?.statusUrl;
    const jobId = started?.job_id || started?.jobId;
    const tick = async () => {
      if (modernizeCancelRef.current) {
        reject(new Error("Modernize cancelled"));
        return;
      }
      try {
        const job = statusUrl
          ? await CodeIntelligenceService.getStatus(statusUrl)
          : jobId
            ? await CodeIntelligenceService.getJob(jobId)
            : null;
        if (!job) {
          reject(new Error("Modernize did not return a status url."));
          return;
        }
        if (
          job?.status === API_RESPONSE.FAILED ||
          isFailedStatus(job?.status) ||
          (typeof job?.error === "string" && job.error)
        ) {
          reject(new Error(readApiError(job, "Modernize failed.")));
          return;
        }
        setModernizeStatus(String(job?.stage_detail || job?.stageDetail || job?.stage || "Working…"));
        if (isCompletedStatus(job?.status)) {
          resolve(job);
          return;
        }
        modernizePollRef.current = setTimeout(tick, MODERNIZE_POLL_MS);
      } catch (err) {
        reject(err);
      }
    };
    tick();
  }), []);

  const handleModernize = useCallback(async () => {
    const id = resolvedProductId;
    if (!id || modernizing || reanalyzing) return;
    modernizeCancelRef.current = false;
    if (modernizePollRef.current) {
      clearTimeout(modernizePollRef.current);
      modernizePollRef.current = null;
    }
    setModernizeOpen(true);
    setModernizing(true);
    setModernizeError("");
    setModernizeResult(null);
    setModernizeStatus("Starting modernization…");
    try {
      const started = await CodeIntelligenceService.startModernize(id);
      if (isFailedStatus(started?.status)) {
        throw new Error(readApiError(started, "Failed to start modernize."));
      }
      if (!isCompletedStatus(started?.status) && (started?.status_url || started?.statusUrl || started?.job_id || started?.jobId)) {
        setModernizeStatus(started?.stage_detail || started?.stageDetail || "Queued…");
        await waitForModernizeJob(started);
      }
      if (modernizeCancelRef.current) return;
      setModernizeStatus("Loading result…");
      const result = await CodeIntelligenceService.getModernize(id);
      if (isFailedStatus(result?.status) && !modernizeDocumentation(result)) {
        throw new Error(readApiError(result, "Unable to load the modernize result."));
      }
      if (!modernizeDocumentation(result) && !buildModernizeJson(result)) {
        throw new Error("Modernize completed, but no result was returned.");
      }
      setModernizeResult(result);
      setModernizeStatus("Completed");
    } catch (err) {
      if (modernizeCancelRef.current) return;
      setModernizeError(readApiError(err, "Modernize failed. Please try again."));
      setModernizeStatus("Failed");
    } finally {
      if (!modernizeCancelRef.current) setModernizing(false);
    }
  }, [modernizing, reanalyzing, resolvedProductId, waitForModernizeJob]);

  const copyModernizeJson = async () => {
    if (!modernizeJsonText) return;
    try {
      await navigator.clipboard.writeText(modernizeJsonText);
      setJsonCopied(true);
      window.setTimeout(() => setJsonCopied(false), 1600);
    } catch {
      showNotificationGlobal("Couldn’t copy the JSON.", "error");
    }
  };

  const sendModernizeToChat = () => {
    const projectId =
      xrayProjectId ||
      application.project_id ||
      application.projectId ||
      "";
    if (!projectId) {
      showNotificationGlobal("This scan is not linked to an Xray project yet.", "error");
      return;
    }
    if (!modernizeJsonText) {
      showNotificationGlobal("Modernize has no JSON to send yet.", "error");
      return;
    }
    let json = modernizeJsonText;
    try {
      json = JSON.parse(modernizeJsonText);
    } catch {
      json = modernizeJsonText;
    }
    const prompt = `Apply this modernization in the project. Use this JSON:\n\n${modernizeJsonText}`;
    const findingChatDraft = stageFindingChatDraft({
      prompt,
      openCode: false,
      xrayJobType: XRAY_JOB_TYPE.MODERNIZE,
      json,
    });
    navigate(`/project/${projectId}?section=frontend`, {
      state: {
        findingChat: true,
        openChat: true,
        openCode: false,
        openProjectView: true,
        findingChatDraft,
      },
    });
  };

  const sendFindingToXrayChat = async (finding) => {
    if (!finding) return;
    let projectId =
      xrayProjectId ||
      application.project_id ||
      application.projectId ||
      "";
    if (!projectId) {
      showNotificationGlobal("This scan is not linked to an Xray project yet.", "error");
      return;
    }
    const findingChatDraft = stageFindingChatDraft({
      prompt: formatFindingPrompt(finding),
      file: findingPath(finding),
      line: findingLine(finding),
      openCode: true,
      xrayJobType: XRAY_JOB_TYPE.FIX,
      json: finding,
    });
    navigate(`/project/${projectId}?section=frontend`, {
      state: { findingChat: true, openCode: true, findingChatDraft },
    });
  };

  const visibleFindings = useMemo(() => {
    if (findingSev === "all") return findings;
    return findings.filter((f) => String(f.severity || "").toLowerCase() === findingSev);
  }, [findings, findingSev]);
  const pagedFindings = usePaged(visibleFindings, 8, findingSev);
  const risk = String(health.risk_level || "unknown").toLowerCase();

  const maxCall = Math.max(1, ...callBars.map((c) => c.count));
  const archNodes = graph.architecture?.nodes || [];
  const archEdges = graph.architecture?.edges || [];
  const tabCounts = {
    architecture: typeScores.length || (primaryType ? 1 : 0),
    modules: modules.length,
    findings: findings.length,
    discovery:
      (discovery.apis || []).length +
      (discovery.databases || []).length +
      (discovery.external_services || []).length +
      (discovery.cloud || []).length +
      dependencies.length,
    files: files.length,
    symbols: symbols.length,
    graph: (callEdges || []).length,
  };

  if (loading && !data) {
    return <CiInsightsLoading projectName={projectName} />;
  }

  if (!data) {
    return (
      <div className="ci-empty" role="status">
        Insights are not available yet. Refresh this page after analysis completes.
      </div>
    );
  }

  const kpiStrip = (
    <div className="ci-kpi-strip">
      <button type="button" className="ci-kpi-chip" onClick={() => setTab("files")}>
        <div className="label">Files</div>
        <div className="value">{formatCount(inventory.files || application.files)}</div>
      </button>
      <button type="button" className="ci-kpi-chip" onClick={() => setTab("modules")}>
        <div className="label">Modules</div>
        <div className="value">{formatCount(modules.length || metrics.business_modules)}</div>
      </button>
      <button type="button" className="ci-kpi-chip" onClick={() => setTab("findings")}>
        <div className="label">Findings</div>
        <div className="value">{formatCount(health.total_findings || findings.length)}</div>
      </button>
      <button type="button" className="ci-kpi-chip" onClick={() => setTab("architecture")}>
        <div className="label">Architecture</div>
        <div className="value">{primaryType ? prettyLabel(primaryType) : "—"}</div>
      </button>
    </div>
  );

  return (
    <>
      {kpiSlot ? createPortal(kpiStrip, kpiSlot) : null}
      <div className={`ci-dash${tab === "files" ? " is-files" : ""}`}>
      {tab !== "files" && tab !== "architecture" ? (
        <>
      <section className="ci-story">
        <div className="ci-story-main">
          <HealthRing score={health.overall} />
          <div className="ci-story-copy">
            <div className="ci-story-kicker">Insights ready</div>
            <div className="ci-story-title-row">
              <h2>{displayName}</h2>
              <div className="ci-story-actions">
                {onRefresh ? (
                  <button
                    type="button"
                    className={`ci-refresh-btn${refreshing ? " is-busy" : ""}`}
                    onClick={onRefresh}
                    disabled={refreshing || reanalyzing}
                    aria-busy={refreshing}
                    title={refreshing ? "Refreshing insights" : "Refresh insights"}
                  >
                    <FiRefreshCw size={12} className={refreshing ? "is-spinning" : undefined} aria-hidden="true" />
                    {refreshing ? "Refreshing…" : "Refresh"}
                  </button>
                ) : null}
                {canReanalyze || onReanalyze ? (
                  <button
                    type="button"
                    className={`ci-reanalyze-btn${reanalyzing ? " is-busy" : ""}`}
                    onClick={() => setConfirmReanalyze(true)}
                    disabled={reanalyzing || refreshing || !onReanalyze}
                    aria-busy={reanalyzing}
                    title={reanalyzing ? "Reanalysis in progress" : "Reanalyze this codebase"}
                  >
                    <FiRefreshCw size={12} className={reanalyzing ? "is-spinning" : undefined} aria-hidden="true" />
                    {reanalyzing ? "Reanalyzing…" : "Reanalyze"}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`ci-modernize-btn${modernizing ? " is-busy" : ""}`}
                  onClick={handleModernize}
                  disabled={modernizing || reanalyzing || refreshing || !resolvedProductId}
                  aria-busy={modernizing}
                  title={resolvedProductId ? "Compress this analysis" : "This scan is not linked to a product yet"}
                >
                  <FiZap size={12} className={modernizing ? "is-spinning" : undefined} aria-hidden="true" />
                  {modernizing ? "Modernizing…" : "Modernize"}
                </button>
              </div>
            </div>
            <p>
              {primaryType ? `${prettyLabel(primaryType)} · ` : ""}
              {risk !== "unknown" ? `${risk} risk` : "Health scored"}
              {health.total_findings || findings.length
                ? ` · ${formatCount(health.total_findings || findings.length)} findings`
                : ""}
              {languages[0]?.name ? ` · mostly ${languages[0].name}` : ""}
              {inventory.files || application.files
                ? ` · ${formatCount(inventory.files || application.files)} files`
                : ""}
            </p>
            <span className={`ci-risk is-${risk}`}>{risk} risk</span>
            {primaryType ? <span className="ci-arch-pill">{prettyLabel(primaryType)}</span> : null}
            {Object.keys(severityCounts).length ? (
              <div className="ci-sev-mini">
                {["critical", "high", "medium", "low"].map((k) =>
                  severityCounts[k] ? (
                    <button
                      type="button"
                      key={k}
                      className={`ci-sev ${k}`}
                      onClick={() => {
                        setFindingSev(k);
                        setTab("findings");
                      }}
                    >
                      {severityCounts[k]} {k}
                    </button>
                  ) : null
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className="ci-cats">
          {["security", "quality", "architecture", "dependency", "compliance"].map((key) => {
            const score = Math.round(categories[key] || 0);
            return (
              <div className="ci-cat" key={key}>
                <div className="ci-cat-top">
                  <span className="ci-cat-name">{key}</span>
                  <span className={`ci-cat-score ${barTone(score)}`}>{score}</span>
                </div>
                <div className="ci-cat-track" aria-hidden="true">
                  <div
                    className={`ci-cat-fill ${barTone(score)}`}
                    style={{ width: `${Math.max(0, Math.min(100, score))}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>
        </>
      ) : null}

      <div className="ci-tabs" role="tablist" aria-label="Result sections">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} type="button" className={tab === id ? "is-active" : ""} onClick={() => setTab(id)}>
            <Icon size={14} />
            {label}
            {tabCounts[id] ? <span className="ci-tab-count">{formatCount(tabCounts[id])}</span> : null}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <div className="ci-grid-3 ci-overview">
          <article className="ci-card ci-overview-card">
            <h3 className="ci-card-title">
              Languages <span className="muted">{languages.length}</span>
            </h3>
            <div className="ci-overview-body">
              {languages.length ? (
                <div className="ci-lang">
                  <div className="ci-lang-stack" aria-hidden="true">
                    {languages.map((lang, i) => (
                      <span
                        key={lang.name}
                        style={{
                          width: `${Math.max(2, lang.percentage || 0)}%`,
                          background: LANG_COLORS[i % LANG_COLORS.length],
                        }}
                      />
                    ))}
                  </div>
                  {languages.map((lang, i) => (
                    <div className="ci-lang-row" key={lang.name}>
                      <div className="ci-lang-name">
                        <FileTypeIcon language={lang.name} path={lang.name} />
                        {lang.name}
                      </div>
                      <div className="ci-lang-track">
                        <div
                          className="ci-lang-fill"
                          style={{
                            width: `${Math.max(4, Math.min(100, lang.percentage || 0))}%`,
                            background: LANG_COLORS[i % LANG_COLORS.length],
                          }}
                        />
                      </div>
                      <div className="ci-lang-pct">{lang.percentage}%</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ci-empty">No language breakdown yet.</div>
              )}
              {frameworks.length ? (
                <div className="ci-chips">
                  {frameworks.map((fw) => (
                    <span className="ci-chip" key={fw.name || fw}>{fw.name || fw}</span>
                  ))}
                </div>
              ) : null}
            </div>
            {primaryType ? (
              <button type="button" className="ci-arch-summary" onClick={() => setTab("architecture")}>
                <div>
                  <div className="label">Detected architecture</div>
                  <div className="value">{prettyLabel(primaryType)}</div>
                  <div className="hint">{architectureConfidence}% confidence{legacy.severity ? ` · ${legacy.severity} legacy` : ""}</div>
                </div>
                <FiChevronRight size={16} aria-hidden="true" />
              </button>
            ) : null}
          </article>

          <article className="ci-card ci-overview-card">
            <h3 className="ci-card-title">Reviewers</h3>
            {reviews.length ? (
              <div className="ci-reviews ci-overview-body">
                {reviews.map((r) => (
                  <button
                    type="button"
                    className="ci-review"
                    key={r.reviewer}
                    onClick={() => setTab("findings")}
                  >
                    <div className={`ci-review-icon${r.findings ? " has-findings" : ""}`}>
                      {r.findings ? <FiAlertCircle size={15} /> : <FiCheckCircle size={15} />}
                    </div>
                    <div className="ci-review-copy">
                      <div className="ci-review-name">{prettyReviewer(r.reviewer)}</div>
                      <div className="ci-review-sum">{r.summary || (r.findings ? "Needs review" : "Clear")}</div>
                    </div>
                    <span className={`ci-review-count${r.findings ? " has-findings" : ""}`}>
                      {r.findings || 0}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="ci-empty ci-overview-body">Reviews have not run yet.</div>
            )}
          </article>

          <article className="ci-card ci-overview-card">
            <h3 className="ci-card-title">Discovery</h3>
            <div className="ci-disc-tiles ci-overview-body">
              {[
                ["apis", "APIs", FiGlobe],
                ["databases", "Databases", FiDatabase],
                ["external_services", "External", FiPackage],
                ["messaging", "Messaging", FiMessageSquare],
                ["cloud", "Cloud", FiCloud],
              ].map(([key, label, Icon], index, list) => {
                const count = (discovery[key] || []).length || inventory[key] || 0;
                const isLast = index === list.length - 1;
                return (
                  <button
                    type="button"
                    className={`ci-disc-tile${count ? " has-items" : ""}${isLast ? " is-span" : ""}`}
                    key={key}
                    onClick={() => setTab("discovery")}
                  >
                    <span className="ci-disc-tile-icon" aria-hidden="true">
                      <Icon size={15} />
                    </span>
                    <span className="ci-disc-tile-copy">
                      <span className="value">{count}</span>
                      <span className="label">{label}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </article>
        </div>
      ) : null}

      {tab === "architecture" ? (
        <div className="ci-arch">
          <section className="ci-arch-hero">
            <div className="ci-arch-hero-copy">
              <div className="ci-story-kicker">Architecture assessment</div>
              <h3>{prettyLabel(primaryType || "unknown")}</h3>
              <div className="ci-arch-meta">
                {architectureConfidence ? (
                  <span className="ci-arch-meta-pill is-score">{architectureConfidence}% confidence</span>
                ) : (
                  <span className="ci-arch-meta-pill">Confidence unavailable</span>
                )}
                {legacy.severity ? (
                  <span className={`ci-sev ${String(legacy.severity).toLowerCase()}`}>
                    {prettyLabel(legacy.severity)} legacy
                  </span>
                ) : null}
                {legacy.modernization_complexity ? (
                  <span className="ci-arch-meta-pill">
                    {prettyLabel(legacy.modernization_complexity)} to modernize
                  </span>
                ) : null}
                <span className="ci-arch-meta-pill">{typeScores.length} patterns</span>
                <span className="ci-arch-meta-pill">{(legacy.characteristics || []).length} issues</span>
                <span className="ci-arch-meta-pill">{signalFlags.length} signals</span>
              </div>
              {(assessment.characteristics || []).length ? (
                <div className="ci-chips ci-chips-tight">
                  {(assessment.characteristics || []).slice(0, 8).map((item) => (
                    <span className="ci-chip" key={item}>{item}</span>
                  ))}
                </div>
              ) : null}
            </div>
            <MiniRing score={architectureConfidence} label="Confidence" />
          </section>

          <div className="ci-arch-grid">
            <article className="ci-card">
              <h3 className="ci-card-title">
                Pattern scores
                <span className="muted">Best match first</span>
              </h3>
              {typeScores.length ? (
                <div className="ci-arch-ranks">
                  {typeScores.slice(0, 8).map((item, i) => {
                    const type = item.application_type || item.type;
                    const score = asPercent(item.normalized_score);
                    const lead = i === 0;
                    return (
                      <div className={`ci-arch-rank${lead ? " is-lead" : ""}`} key={type}>
                        <span className="ci-arch-rank-n">{i + 1}</span>
                        <strong>{prettyLabel(type)}</strong>
                        <div className="ci-bar-track">
                          <div className={`ci-bar-fill ${barTone(score)}`} style={{ width: `${Math.max(4, score)}%` }} />
                        </div>
                        <em>{score}%</em>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="ci-empty">No architecture scores yet.</div>
              )}
            </article>

            <article className="ci-card">
              <h3 className="ci-card-title">
                Legacy issues
                {legacy.severity ? <span className={`ci-sev ${String(legacy.severity).toLowerCase()}`}>{legacy.severity}</span> : null}
              </h3>
              {(legacy.characteristics || []).length ? (
                <div className="ci-legacy-list">
                  {legacy.characteristics.map((item) => {
                    const sev = String(item.severity || "low").toLowerCase();
                    return (
                      <div className={`ci-legacy-item is-${sev}`} key={item.name}>
                        <div className="ci-legacy-top">
                          <strong>{prettyLabel(item.name)}</strong>
                          <span className={`ci-sev ${sev}`}>{item.severity || "low"}</span>
                        </div>
                        {(item.evidence || []).slice(0, 1).map((ev, i) => (
                          <div className="ci-finding-desc" key={i}>{ev.explanation || ev.signal}</div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="ci-empty">No legacy characteristics detected.</div>
              )}
            </article>
          </div>

          <div className="ci-arch-grid">
            {(assessment.evidence || []).length ? (
              <article className="ci-card ci-arch-why">
                <h3 className="ci-card-title">
                  Why this match
                  <span className="muted">{assessment.evidence.length}</span>
                </h3>
                <div className="ci-arch-evidence">
                  {assessment.evidence.map((ev, i) => (
                    <div className="ci-evidence-row" key={ev.rule_id || i}>
                      <div className="ci-finding-title">{prettyLabel(ev.signal)}</div>
                      <div className="ci-finding-desc">{ev.explanation || "Matched architecture signal."}</div>
                      {ev.rule_id ? <div className="ci-evidence-id">{ev.rule_id}</div> : null}
                    </div>
                  ))}
                </div>
              </article>
            ) : null}

            <article className="ci-card">
              <h3 className="ci-card-title">
                What to do next
                <span className="muted">{architectureRecs.length || ""}</span>
              </h3>
              {architectureRecs.length ? (
                <ol className="ci-rec-list">
                  {architectureRecs.map((item, i) => {
                    const rec = recParts(item);
                    return (
                      <li key={`${rec.body}-${i}`}>
                        <div>
                          {rec.tag ? <span className="ci-badge">{rec.tag}</span> : null}
                          <span>{rec.body}</span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="ci-empty">No recommendations yet.</div>
              )}
            </article>
          </div>

          <article className="ci-card ci-arch-signals">
            <h3 className="ci-card-title">
              Detected signals
              <span className="muted">{signalFlags.length}</span>
            </h3>
            {signalGroups.length ? (
              <div className="ci-arch-sig-groups">
                {signalGroups.map(([label, items]) => (
                  <div className="ci-arch-sig-group" key={label}>
                    <div className="ci-arch-sig-label">{label}</div>
                    <div className="ci-chips ci-chips-tight">
                      {items.map((flag) => (
                        <span className="ci-chip" key={flag}>{flag}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ci-empty">No architecture signals yet.</div>
            )}
          </article>
        </div>
      ) : null}

      {tab === "modules" ? (
        <article className="ci-card">
          <h3 className="ci-card-title">
            Business modules
            <span className="muted">
              {modules.length} detected
              {Array.isArray(businessModulesRaw.unclassified_files)
                ? ` · ${businessModulesRaw.unclassified_files.length} unclassified files`
                : ""}
            </span>
          </h3>
          {modules.length ? (
            <div className="ci-modules">
              {modules.map((mod) => {
                const id = mod.id || mod.name;
                const open = openModule === id;
                const filesForMod = asList(mod.files);
                const coupling = String(mod.coupling || "unknown").toLowerCase();
                return (
                  <section className={`ci-module${open ? " is-open" : ""}`} key={id}>
                    <button type="button" className="ci-module-head" onClick={() => setOpenModule(open ? null : id)}>
                      <div>
                        <div className="ci-module-name">{mod.name || prettyLabel(id)}</div>
                        <div className="ci-module-meta">
                          {asPercent(mod.confidence)}% confidence
                          {filesForMod.length ? ` · ${filesForMod.length} files` : ""}
                          {mod.symbols ? ` · ${formatCount(mod.symbols)} symbols` : ""}
                          {mod.modernization_candidate ? " · modernization candidate" : ""}
                        </div>
                      </div>
                      <div className="ci-module-tags">
                        {mod.coupling ? <span className={`ci-sev ${coupling === "high" || coupling === "medium" ? coupling : "low"}`}>{coupling}</span> : null}
                        <span className="ci-badge">{asPercent(mod.confidence)}%</span>
                      </div>
                    </button>
                    {open ? (
                      <div className="ci-module-body">
                        {mod.description ? <p className="ci-finding-desc">{mod.description}</p> : null}
                        {mod.modernization_reason ? <p className="ci-finding-desc">{mod.modernization_reason}</p> : null}
                        <div className="ci-disc">
                          <div className="ci-disc-item">
                            <div className="label">Symbols</div>
                            <div className="value">{formatCount(mod.symbols)}</div>
                          </div>
                          <div className="ci-disc-item">
                            <div className="label">Functions</div>
                            <div className="value">{formatCount(mod.functions)}</div>
                          </div>
                          <div className="ci-disc-item">
                            <div className="label">APIs</div>
                            <div className="value">{formatCount(asList(mod.apis).length)}</div>
                          </div>
                          <div className="ci-disc-item">
                            <div className="label">Dependencies</div>
                            <div className="value">{formatCount(asList(mod.dependencies).length)}</div>
                          </div>
                        </div>
                        {asList(mod.internal_dependencies).length ? (
                          <div className="ci-chips">
                            {asList(mod.internal_dependencies).map((dep) => (
                              <span className="ci-chip" key={dep}>{dep}</span>
                            ))}
                          </div>
                        ) : null}
                        {filesForMod.length ? (
                          <div className="ci-table-wrap ci-block">
                            <table className="ci-table">
                              <thead>
                                <tr>
                                  <th>File</th>
                                  <th>Role</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filesForMod.slice(0, 40).map((file, i) => (
                                  <tr key={file.path || i}>
                                    <td>
                                      <FilePath path={file.path || file} />
                                    </td>
                                    <td>{file.role || "—"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="ci-empty">No business modules detected.</div>
          )}
        </article>
      ) : null}

      {tab === "findings" ? (
        <article className="ci-card">
          <h3 className="ci-card-title">
            Findings
            <span className="muted">
              {severityCounts.critical ? `${severityCounts.critical} critical · ` : ""}
              {severityCounts.high ? `${severityCounts.high} high · ` : ""}
              {visibleFindings.length} shown
            </span>
          </h3>
          <div className="ci-toolbar">
            {["all", "critical", "high", "medium", "low"].map((k) => (
              <button
                type="button"
                key={k}
                className={`ci-pill-filter${findingSev === k ? " is-on" : ""}`}
                onClick={() => setFindingSev(k)}
              >
                {k === "all" ? "All" : k}
                {k !== "all" && severityCounts[k] ? ` ${severityCounts[k]}` : ""}
              </button>
            ))}
            <span className="count">{visibleFindings.length.toLocaleString()} findings</span>
          </div>
          {pagedFindings.slice.length ? (
            pagedFindings.slice.map((f, i) => {
              const sev = String(f.severity || "low").toLowerCase();
              const evidence = asList(f.evidence);
              const path = findingPath(f);
              const line = findingLine(f);
              return (
                <article className={`ci-finding is-${sev}`} key={f.id || i}>
                  <div className="ci-finding-top">
                    <div className="ci-finding-title">
                      {f.title || f.message || "Finding"}{" "}
                      {f.rule_id ? <span className="ci-badge">{f.rule_id}</span> : null}
                    </div>
                    <div className="ci-finding-actions">
                      <span className={`ci-sev ${sev}`}>{sev}</span>
                      <button
                        type="button"
                        className="ci-finding-chat-btn"
                        onClick={() => sendFindingToXrayChat(f)}
                        title="Open this finding in the Xray project chat"
                      >
                        <FiSend size={13} />
                        Fix it
                      </button>
                    </div>
                  </div>
                  <div className="ci-finding-meta">
                    {path ? <FilePath path={path} onClick={() => openFindingLocation(f)} /> : null}
                    {line ? (
                      <>
                        {" · "}
                        <button type="button" className="ci-line-link" onClick={() => openFindingLocation(f)}>
                          line {line}
                        </button>
                      </>
                    ) : null}
                    {f.category ? ` · ${f.category}` : ""}
                  </div>
                  {f.description ? <div className="ci-finding-desc">{f.description}</div> : null}
                  {evidence.slice(0, 3).map((ev, ei) => (
                    <div className="ci-evidence" key={ei}>
                      {typeof ev === "string" ? ev : ev.message || ev.snippet || ""}
                    </div>
                  ))}
                  {f.remediation ? <div className="ci-finding-desc">{f.remediation}</div> : null}
                </article>
              );
            })
          ) : (
            <div className="ci-empty">No findings reported.</div>
          )}
          <Pager page={pagedFindings.page} pages={pagedFindings.pages} onPage={pagedFindings.setPage} total={pagedFindings.total} noun="findings" />
        </article>
      ) : null}

      {tab === "discovery" ? (
        <div className="ci-disc-view">
          <nav className="ci-disc-nav" aria-label="Discovery categories">
            {[
              ["ci-disc-apis", "APIs", (discovery.apis || []).length],
              ["ci-disc-databases", "Databases", (discovery.databases || []).length],
              ["ci-disc-cloud", "Cloud", (discovery.cloud || []).length],
              ["ci-disc-messaging", "Messaging", (discovery.messaging || []).length],
              ["ci-disc-deps", "Dependencies", dependencies.length],
            ].map(([id, label, count]) => (
              <a key={id} className={`ci-disc-chip${count ? " has-items" : ""}`} href={`#${id}`}>
                <strong>{label}</strong>
                <span>{count.toLocaleString()}</span>
              </a>
            ))}
          </nav>

          <DiscoveryPanel
            id="ci-disc-apis"
            icon={FiGlobe}
            title="API endpoints"
            hint="Routes and handlers found in the codebase"
            count={(discovery.apis || []).length}
          >
            <FilterTable
              items={discovery.apis || []}
              placeholder="Filter endpoints…"
              noun="endpoints"
              empty="No API endpoints detected."
              columns={[
                { key: "method", label: "Method", render: (r) => (
                  <span className={`ci-method ${methodClass(r.method || r.verb)}`}>{r.method || r.verb || "—"}</span>
                ) },
                { key: "path", label: "Path", className: "ci-path", render: (r) => r.path || r.route || r.url || "—" },
                { key: "file", label: "File", render: (r) => <FilePath path={r.file || r.source} /> },
              ]}
            />
          </DiscoveryPanel>

          <DiscoveryPanel
            id="ci-disc-databases"
            icon={FiDatabase}
            title="Databases"
            hint="Engines, ORMs, and data stores"
            count={(discovery.databases || []).length}
          >
            <FilterTable
              items={discovery.databases || []}
              placeholder="Filter databases…"
              noun="databases"
              empty="No databases detected."
              columns={[
                { key: "name", label: "Name", render: (r) => r.technology || r.name || r.engine || "—" },
                { key: "file", label: "File", render: (r) => <FilePath path={r.file || r.path} /> },
              ]}
            />
          </DiscoveryPanel>

          <DiscoveryPanel
            id="ci-disc-cloud"
            icon={FiCloud}
            title="Cloud"
            hint="Providers and cloud services in use"
            count={(discovery.cloud || []).length}
          >
            <FilterTable
              items={discovery.cloud || []}
              placeholder="Filter cloud services…"
              noun="services"
              empty="No cloud services detected."
              columns={[
                { key: "provider", label: "Provider", render: (r) => r.provider || r.name || "—" },
                { key: "file", label: "File", render: (r) => <FilePath path={r.file || r.path} /> },
              ]}
            />
          </DiscoveryPanel>

          <DiscoveryPanel
            id="ci-disc-messaging"
            icon={FiMessageSquare}
            title="Messaging"
            hint="Queues, brokers, and event buses"
            count={(discovery.messaging || []).length}
          >
            <FilterTable
              items={discovery.messaging || []}
              placeholder="Filter messaging…"
              noun="systems"
              empty="No messaging systems detected."
              columns={[
                { key: "name", label: "Name", render: (r) => r.technology || r.name || r.system || "—" },
                { key: "file", label: "File", render: (r) => <FilePath path={r.file || r.path} /> },
              ]}
            />
          </DiscoveryPanel>

          <DiscoveryPanel
            id="ci-disc-deps"
            icon={FiPackage}
            title="Dependencies"
            hint="Packages and ecosystems indexed from manifests"
            count={dependencies.length}
          >
            <FilterTable
              items={dependencies}
              placeholder="Filter packages…"
              noun="dependencies"
              empty="No dependencies indexed yet."
              columns={[
                { key: "name", label: "Package", className: "ci-path", render: (r) => r.name || r.package || "—" },
                { key: "version", label: "Version", render: (r) => r.version || "—" },
                { key: "ecosystem", label: "Ecosystem", render: (r) => <span className="ci-badge">{r.ecosystem || r.manifest || "—"}</span> },
              ]}
            />
          </DiscoveryPanel>
        </div>
      ) : null}

      {tab === "files" ? (
        <article className="ci-card ci-files-workspace">
          {visibleFiles.length ? (
            <div className="ci-files-split" id="ci-files-panel">
              <aside className="ci-files-tree" aria-label="File tree">
                <div className="ci-files-tree-head">
                  <div className="ci-files-tree-title">
                    Explorer
                    <span>{visibleFiles.length.toLocaleString()}</span>
                  </div>
                  <div className="ci-search">
                    <FiSearch className="ci-search-icon" size={14} />
                    <input type="search" placeholder="Search files…" value={fileQuery} onChange={(e) => setFileQuery(e.target.value)} />
                  </div>
                  <div className="ci-files-tree-filters">
                    <select value={fileLang} onChange={(e) => setFileLang(e.target.value)}>
                      <option value="all">All languages</option>
                      {languagesInFiles.map((lang) => (
                        <option key={lang} value={lang}>{lang}</option>
                      ))}
                    </select>
                    <button type="button" className={`ci-toggle${showGit ? " is-on" : ""}`} onClick={() => setShowGit((v) => !v)}>
                      {showGit ? "Hide .git" : `.git${gitCount ? ` ${gitCount}` : ""}`}
                    </button>
                  </div>
                </div>
                <div className="ci-files-tree-body" ref={treeBodyRef}>
                  {fileTree.map((node) => (
                    <FileTreeNode
                      key={node.path}
                      node={node}
                      expanded={expandedFolders}
                      selectedPath={selectedFile?.path}
                      onToggle={toggleFolder}
                      onOpen={handleFileOpen}
                    />
                  ))}
                </div>
              </aside>
              <section className="ci-files-preview">
                {selectedFile ? (
                  <>
                    <header className="ci-files-preview-head">
                      <div className="ci-files-tab">
                        <FileTypeIcon path={selectedFile.path} language={selectedFile.language} />
                        <strong>{selectedFile.path.split("/").pop()}</strong>
                        {selectedFile.language ? <span className="ci-files-tab-lang">{selectedFile.language}</span> : null}
                        <span className="ci-files-tab-meta">
                          {selectedFile.lines ? `${selectedFile.lines.toLocaleString()} lines` : null}
                          {selectedFile.lines && selectedFile.size ? " · " : null}
                          {selectedFile.size ? formatBytes(selectedFile.size) : null}
                          {highlightLine ? ` · line ${highlightLine}` : null}
                        </span>
                      </div>
                      <FileBreadcrumb path={selectedFile.path} />
                    </header>
                    {fileContentLoading ? (
                      <div className="ci-files-preview-empty">
                        <FiCode size={26} />
                        <strong>Opening {selectedFile.path.split("/").pop()}</strong>
                        <p>Loading source in the editor…</p>
                      </div>
                    ) : fileContent != null ? (
                      fileContent ? (
                        <CodePreview
                          content={fileContent}
                          path={selectedFile.path}
                          language={selectedFile.language}
                          highlightLine={highlightLine}
                        />
                      ) : (
                        <div className="ci-files-preview-empty">
                          <FiFile size={26} />
                          <strong>Empty file</strong>
                          <p>This file has no content to show.</p>
                        </div>
                      )
                    ) : fileContentError ? (
                      <div className="ci-files-preview-empty">
                        <FiAlertCircle size={26} />
                        <strong>Couldn’t open this file</strong>
                        <p>{fileContentError}</p>
                      </div>
                    ) : (
                      <div className="ci-files-preview-empty">
                        <FiCode size={26} />
                        <strong>Opening source</strong>
                        <p>File content will appear in the editor in a moment.</p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="ci-files-preview-empty">
                    <FiFolder size={28} />
                    <strong>Select a file</strong>
                    <p>Pick a file from the explorer to read it here.</p>
                  </div>
                )}
              </section>
            </div>
          ) : (
            <div className="ci-empty">No matching files.</div>
          )}
        </article>
      ) : null}

      {tab === "symbols" ? (
        <article className="ci-card">
          <h3 className="ci-card-title">Symbols</h3>
          <FilterTable
            items={symbols}
            placeholder="filter symbols…"
            noun="symbols"
            empty="No symbols indexed yet."
            columns={[
              { key: "name", label: "Name", render: (r) => r.name || r.symbol || "—" },
              { key: "kind", label: "Kind", render: (r) => <span className="ci-badge">{r.type || r.kind || "symbol"}</span> },
              { key: "file", label: "File", render: (r) => <FilePath path={r.file || r.path} language={r.language} /> },
            ]}
          />
        </article>
      ) : null}

      {tab === "graph" ? (
        <div className="ci-grid">
          <article className="ci-card">
            <h3 className="ci-card-title">Most-called symbols</h3>
            {callBars.length ? (
              <div className="ci-lang">
                {callBars.map((c) => (
                  <div className="ci-barrow" key={c.target}>
                    <div className="ci-bar-label" title={c.target}>{c.target}</div>
                    <div className="ci-bar-track">
                      <div
                        className={`ci-bar-fill ${barTone((c.count / maxCall) * 100)}`}
                        style={{ width: `${Math.max(4, (c.count / maxCall) * 100)}%` }}
                      />
                    </div>
                    <div className="ci-bar-val">{formatCount(c.count)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ci-empty">No call edges indexed yet.</div>
            )}
          </article>
          <article className="ci-card">
            <h3 className="ci-card-title">Graph totals</h3>
            <div className="ci-disc">
              <div className="ci-disc-item">
                <div className="label">Import edges</div>
                <div className="value">{formatCount(imports.length)}</div>
              </div>
              <div className="ci-disc-item">
                <div className="label">Call edges</div>
                <div className="value">{formatCount((callEdges || []).length)}</div>
              </div>
              <div className="ci-disc-item">
                <div className="label">Architecture nodes</div>
                <div className="value">{formatCount(archNodes.length)}</div>
              </div>
              <div className="ci-disc-item">
                <div className="label">Architecture edges</div>
                <div className="value">{formatCount(archEdges.length)}</div>
              </div>
            </div>
            <h3 className="ci-card-title ci-block">Imports</h3>
            <FilterTable
              items={imports}
              placeholder="filter by module…"
              noun="imports"
              empty="No imports indexed yet."
              columns={[
                { key: "module", label: "Module", className: "ci-path", render: (r) => r.module || "—" },
                { key: "file", label: "File", render: (r) => <FilePath path={r.file} /> },
              ]}
            />
          </article>
        </div>
      ) : null}
    </div>
      {!kpiSlot ? kpiStrip : null}
      {confirmReanalyze
        ? createPortal(
            <div className="ci-confirm" role="dialog" aria-modal="true" aria-labelledby="ci-reanalyze-confirm-title">
              <button
                type="button"
                className="ci-confirm-backdrop"
                aria-label="Close"
                onClick={() => setConfirmReanalyze(false)}
              />
              <div className="ci-confirm-card">
                <div className="ci-confirm-kicker">Xray</div>
                <h3 id="ci-reanalyze-confirm-title">Reanalyze this project?</h3>
                <p>
                  {displayName ? (
                    <>
                      Run a fresh scan for <strong>{displayName}</strong>. Existing source stays in place — no new project is created.
                    </>
                  ) : (
                    <>Run a fresh scan of this codebase. Existing source stays in place — no new project is created.</>
                  )}
                </p>
                <div className="ci-confirm-actions">
                  <button
                    type="button"
                    className="ci-confirm-cancel"
                    onClick={() => setConfirmReanalyze(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ci-confirm-proceed"
                    onClick={() => {
                      setConfirmReanalyze(false);
                      onReanalyze?.();
                    }}
                  >
                    <FiRefreshCw size={13} aria-hidden="true" />
                    Reanalyze
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {modernizeOpen
        ? createPortal(
            <div className="ci-modernize" role="dialog" aria-modal="true" aria-labelledby="ci-modernize-title">
              <button
                type="button"
                className="ci-modernize-backdrop"
                aria-label="Close modernize panel"
                onClick={() => {
                  if (modernizing) return;
                  setModernizeOpen(false);
                }}
              />
              <div className="ci-modernize-panel">
                <div className="ci-modernize-head">
                  <div>
                    <div className="ci-modernize-kicker">Xray · Modernize</div>
                    <h3 id="ci-modernize-title">{displayName || "Modernize"}</h3>
                    <p className="ci-modernize-status">
                      {modernizing ? modernizeStatus || "Working…" : modernizeError ? "Failed" : modernizeResult ? "Ready" : modernizeStatus}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="ci-modernize-close"
                    aria-label="Close"
                    disabled={modernizing}
                    onClick={() => setModernizeOpen(false)}
                  >
                    <FiX size={16} />
                  </button>
                </div>
                {modernizing ? (
                  <div className="ci-modernize-loading" role="status" aria-live="polite">
                    <FiZap size={18} className="is-spinning" aria-hidden="true" />
                    <div>
                      <strong>Compressing the analysis</strong>
                      <span>{modernizeStatus || "This can take a minute…"}</span>
                    </div>
                  </div>
                ) : null}
                {modernizeError && !modernizing ? (
                  <div className="ci-modernize-error" role="alert">
                    <FiAlertCircle size={16} aria-hidden="true" />
                    <div>
                      <strong>Couldn’t finish modernize</strong>
                      <span>{modernizeError}</span>
                    </div>
                    <button type="button" className="ci-modernize-retry" onClick={handleModernize}>
                      Try again
                    </button>
                  </div>
                ) : null}
                {modernizeResult && !modernizing ? (
                  modernizeJsonText ? (
                    <>
                      <div className="ci-modernize-body">
                        <div className="ci-modernize-json-bar">
                          <span>JSON</span>
                          <button type="button" className="ci-modernize-copy" onClick={copyModernizeJson}>
                            {jsonCopied ? <FiCheckCircle size={13} aria-hidden="true" /> : <FiCopy size={13} aria-hidden="true" />}
                            {jsonCopied ? "Copied" : "Copy JSON"}
                          </button>
                        </div>
                        <div className="ci-modernize-json-view">
                          <Editor
                            height="100%"
                            language="json"
                            theme="vs"
                            value={modernizeJsonText}
                            path="modernize.json"
                            options={{
                              readOnly: true,
                              domReadOnly: true,
                              minimap: { enabled: false },
                              lineNumbers: "on",
                              scrollBeyondLastLine: false,
                              wordWrap: "on",
                              fontSize: 13,
                              folding: true,
                              automaticLayout: true,
                              renderLineHighlight: "none",
                              overviewRulerLanes: 0,
                              hideCursorInOverviewRuler: true,
                              scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                            }}
                          />
                        </div>
                      </div>
                      <div className="ci-modernize-foot">
                        <div>
                          <strong>Continue to Modernize</strong>
                          <span>Opens the project and places this JSON in the chat input.</span>
                        </div>
                        <button type="button" className="ci-modernize-fix" onClick={sendModernizeToChat}>
                          <FiMessageSquare size={14} aria-hidden="true" />
                            Continue to Modernize
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="ci-modernize-empty">No JSON returned.</p>
                  )
                ) : null}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default CodeIntelligenceOverview;
